import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { homeFor, useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDate, fmtDateTime } from '../../utils/format.js';
import { checkEmail } from '../../utils/validate.js';

export const ROLE_INFO = {
  owner: { label: 'Owner', tone: 'navy', does: 'Everything, including adding or removing other owners.' },
  admin: { label: 'Admin', tone: 'info', does: 'Manages the company, its domains, channels and team — but can’t change owners.' },
  member: { label: 'Member', tone: 'neutral', does: 'Works on events and the Inbox. Can see the team but not change it.' },
};
const ROLES = ['member', 'admin', 'owner'];

/** Which roles `myRole` may give someone (the server checks the same rules). */
const rolesICanGive = (myRole) => (myRole === 'owner' ? ROLES : myRole === 'admin' ? ['member', 'admin'] : []);

function RolePicker({ value, onChange, allowed, error }) {
  return (
    <fieldset className="fieldset">
      <legend>What can they do?</legend>
      {ROLES.map((r) => {
        const ok = allowed.includes(r);
        return (
          <label key={r} className={`radio-card${value === r ? ' is-selected' : ''}${ok ? '' : ' is-disabled'}`}>
            <input type="radio" name="role" checked={value === r} disabled={!ok} onChange={() => onChange(r)} />
            <span><strong>{ROLE_INFO[r].label}</strong>
              <span className="muted small">{ROLE_INFO[r].does}{!ok && r === 'owner' ? ' Only an owner can make someone an owner.' : ''}</span>
            </span>
          </label>
        );
      })}
      {error && <p className="field-error" role="alert">{error}</p>}
    </fieldset>
  );
}

function InviteModal({ base, team, myEmail, onClose, onSent }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const domains = team.unverified_domains;

  const problem = () => checkEmail(email, { label: 'their email address' })
    || (email.trim().toLowerCase() === String(myEmail).toLowerCase() ? 'That’s your own address. Enter a colleague’s.' : '');

  const send = async () => {
    const p = problem();
    if (p) { setErrors({ email: p }); return; }
    setBusy(true); setError(''); setErrors({});
    try {
      onSent(await api(`${base}/invites`, { method: 'POST', body: { email: email.trim(), role } }));
    } catch (err) {
      if (err.data?.fields) setErrors(err.data.fields); else setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Invite a colleague" onClose={onClose} onSubmit={send} busy={busy}
      description="We’ll email them a link to join. It works for 7 days and only once, and they have to sign in (or create an account) with this exact address to use it."
      footer={<ModalActions submitLabel="Send the invite" busyLabel="Sending…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Their email address" type="email" autoCapitalize="none" autoComplete="off" maxLength={320} value={email}
        onChange={(e) => { setEmail(e.target.value); setErrors({ ...errors, email: undefined }); }}
        onBlur={() => email.trim() && setErrors((x) => ({ ...x, email: problem() || undefined }))}
        placeholder={domains[0] ? `name@${domains[0]}` : 'name@company.com'} error={errors.email}
        hint={!errors.email && domains.length > 0
          ? `Someone with an @${domains.join(' or @')} address also helps prove that domain is yours (option 1 on the domain page).`
          : undefined} />
      <RolePicker value={role} onChange={setRole} allowed={rolesICanGive(team.my_role)} error={errors.role} />
    </Modal>
  );
}

function ChangeRoleModal({ base, member, myRole, onClose, onSaved }) {
  const [role, setRole] = useState(member.role);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (role === member.role) { onClose(); return; }
    setBusy(true); setError('');
    try {
      onSaved(await api(`${base}/members/${member.user_id}`, { method: 'PATCH', body: { role } }));
    } catch (err) { setError(err.message); setBusy(false); }
  };
  return (
    <Modal title={`Change ${member.name || member.email}’s role`} onClose={onClose} onSubmit={save} busy={busy}
      footer={<ModalActions submitLabel="Save role" busyLabel="Saving…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <RolePicker value={role} onChange={setRole} allowed={rolesICanGive(myRole)} />
    </Modal>
  );
}

const ProvesPill = ({ domains }) => (domains.length > 0
  ? <span className="block team-proves"><Pill tone="good" title="A team member who signs in with an address on this domain is the first of the two proofs for verifying it by email.">Helps prove {domains.join(', ')}</Pill></span>
  : null);

/** The company's team: members, their roles, and invites waiting for an answer. */
export default function Team() {
  const auth = useAuth();
  const { organisation: current, user, refresh } = auth;
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [modal, setModal] = useState(null);   // { kind: 'invite' | 'role' | 'remove' | 'cancel', item }
  const [resending, setResending] = useState(null);

  const base = current ? `/portal/organisations/${current.id}/team` : '';
  const load = useCallback(async () => {
    if (!current) return;
    try { setTeam(await api(base)); setError(''); } catch (err) { setError(err.message); }
  }, [base, current]);
  useEffect(() => { load(); }, [load]);

  const back = { to: '/company', label: 'Company' };
  if (error) return <div className="page"><PageHeader back={back} title="Team" /><Alert tone="danger">{error}</Alert></div>;
  if (!team) return <div className="page"><Loading /></div>;

  const close = () => setModal(null);
  const myRole = team.my_role;
  const canTouch = (m) => !m.is_you && (myRole === 'owner' || (myRole === 'admin' && m.role !== 'owner'));
  const canTouchInvite = (i) => team.can_manage && (i.role !== 'owner' || myRole === 'owner');

  const resend = async (i) => {
    setResending(i.id); setNotice(null);
    try {
      const res = await api(`${base}/invites/${i.id}/resend`, { method: 'POST' });
      setTeam({ ...team, ...res });
      setNotice({ tone: 'success', text: `Sent again to ${i.email}. The earlier link no longer works; this one lasts 7 days.` });
    } catch (err) { setNotice({ tone: 'danger', text: err.message }); }
    setResending(null);
  };

  const removeMember = async (m) => {
    const res = await api(`${base}/members/${m.user_id}`, { method: 'DELETE' });
    close();
    if (res.left) {
      const me = await refresh();
      navigate(homeFor(me), { replace: true });
      return;
    }
    setTeam({ ...team, ...res });
    setNotice({ tone: 'success', text: `${m.name || m.email} has been removed from ${current.name}. They can no longer see or change anything here.` });
  };

  const cancelInvite = async (i) => {
    const res = await api(`${base}/invites/${i.id}`, { method: 'DELETE' });
    close();
    setTeam({ ...team, ...res });
    setNotice({ tone: 'success', text: `Invite to ${i.email} cancelled. The link in their email no longer works.` });
  };

  const me = team.members.find((m) => m.is_you);

  return (
    <div className="page">
      <PageHeader back={back} title="Team"
        subtitle={`The people who can sign in and work on ${current.name}. Everyone signs in with their own account, so you can always see who did what.`}
        actions={team.can_manage && <button type="button" className="btn btn-primary" onClick={() => setModal({ kind: 'invite' })}>Invite a colleague</button>} />

      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
      {team.unverified_domains.length > 0 && (
        <Alert tone="info">
          A colleague who signs in with an <strong>@{team.unverified_domains.join(' or @')}</strong> address is the first of the two
          proofs for verifying {team.unverified_domains.length === 1 ? 'that domain' : 'those domains'} by email (option 1 on the domain page).
          {' '}<Link to="/company">See your domains</Link>.
        </Alert>
      )}

      <Section title="Members" description="What each role can do: owners do everything; admins manage the company and team but not owners; members work on events." flush>
        <div className="table-scroll">
          <table className="table table-stack">
            <thead><tr><th>Person</th><th>Role</th><th>Joined</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {team.members.map((m) => (
                <tr key={m.user_id}>
                  <td data-label="Person">
                    <div className="cell-stack">
                      <strong>{m.name || m.email}</strong>{m.is_you && <span className="muted"> (you)</span>}
                      {m.name && <span className="muted small block mono">{m.email}</span>}
                      <ProvesPill domains={m.proves_domains} />
                    </div>
                  </td>
                  <td data-label="Role"><Pill tone={ROLE_INFO[m.role].tone} title={ROLE_INFO[m.role].does}>{ROLE_INFO[m.role].label}</Pill></td>
                  <td data-label="Joined" className="small">{fmtDate(m.joined_at)}</td>
                  <td className="right row-actions">
                    {canTouch(m) && <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ kind: 'role', item: m })}>Change role</button>}
                    {canTouch(m) && <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ kind: 'remove', item: m })}>Remove</button>}
                    {m.is_you && team.members.length > 1 && <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ kind: 'remove', item: m })}>Leave</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Invites waiting for an answer"
        description="Each link works for 7 days and only once. Resending sends a new link and the old one stops working."
        flush={team.invites.length > 0}>
        {team.invites.length === 0 ? (
          <EmptyState>{team.can_manage ? 'No invites waiting. Use “Invite a colleague” to add someone.' : 'No invites waiting.'}</EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Sent to</th><th>Role</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {team.invites.map((i) => (
                  <tr key={i.id}>
                    <td data-label="Sent to">
                      <div className="cell-stack">
                        <span className="mono">{i.email}</span>
                        {i.invited_by && <span className="muted small block">Invited by {i.invited_by}</span>}
                        <ProvesPill domains={i.proves_domains} />
                      </div>
                    </td>
                    <td data-label="Role"><Pill tone={ROLE_INFO[i.role].tone}>{ROLE_INFO[i.role].label}</Pill></td>
                    <td data-label="Status" className="small">
                      <div className="cell-stack">
                        {i.expired
                          ? <><Pill tone="warn">Expired</Pill><span className="muted block">The link no longer works. Resend to give them a new one.</span></>
                          : <><Pill tone="info">Waiting</Pill><span className="muted block">Sent {fmtDateTime(i.last_sent_at)} · link works until {fmtDate(i.expires_at)}</span></>}
                      </div>
                    </td>
                    <td className="right row-actions">
                      {canTouchInvite(i) && (
                        <>
                          <button type="button" className="btn btn-small btn-secondary" disabled={resending === i.id || i.sends_left === 0}
                            title={i.sends_left === 0 ? 'Sent as many times as we allow. Cancel it and check the address.' : undefined}
                            onClick={() => resend(i)}>{resending === i.id ? 'Sending…' : 'Resend'}</button>
                          <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ kind: 'cancel', item: i })}>Cancel invite</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {modal?.kind === 'invite' && (
        <InviteModal base={base} team={team} myEmail={user.email} onClose={close}
          onSent={(res) => { close(); setTeam({ ...team, ...res }); setNotice({ tone: 'success', text: `Invite sent to ${res.email}. They’ll appear under Members once they accept.` }); }} />
      )}
      {modal?.kind === 'role' && (
        <ChangeRoleModal base={base} member={modal.item} myRole={myRole} onClose={close}
          onSaved={(res) => { close(); setTeam({ ...team, ...res }); setNotice({ tone: 'success', text: `${modal.item.name || modal.item.email}’s role is updated. It applies straight away.` }); }} />
      )}
      {modal?.kind === 'remove' && (
        <ConfirmModal danger title={modal.item.is_you ? `Leave ${current.name}?` : `Remove ${modal.item.name || modal.item.email}?`}
          confirmLabel={modal.item.is_you ? 'Leave the company' : 'Remove from team'} onClose={close} onConfirm={() => removeMember(modal.item)}>
          <p>{modal.item.is_you
            ? `You’ll lose access to ${current.name} straight away. Someone in the team would have to invite you again to come back.`
            : `They’ll lose access to ${current.name} straight away. Their account stays, and anything they did here stays in the history.`}</p>
          {modal.item.proves_domains.length > 0 && me && (
            <p className="muted small">Domains already verified stay verified. They no longer count as a team member for domains you haven&rsquo;t verified yet.</p>
          )}
        </ConfirmModal>
      )}
      {modal?.kind === 'cancel' && (
        <ConfirmModal title={`Cancel the invite to ${modal.item.email}?`} confirmLabel="Cancel invite" cancelLabel="Keep it" onClose={close}
          onConfirm={() => cancelInvite(modal.item)}>
          <p>The link in their email will stop working. You can invite them again later.</p>
        </ConfirmModal>
      )}
    </div>
  );
}
